import { Component, DestroyRef, OnInit, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { SnackbarService } from '../../../../utilities/snackbar.service';
import { FilterCardFieldInputComponent } from '../filter-card-field-input/filter-card-field-input.component';
import { TFilterCardFieldConfig } from '../filter-card.interface';

/** The trailing "+ Add Filter" chip: a field picker menu, then that field's value input. Mirrors
 * digital-boards' `AdvancedFilterInputsToggleChipComponent`, minus its workspace-module submenus —
 * action-tracker's pool is a flat list. */
@Component({
  imports: [FilterCardFieldInputComponent, MatButton, MatIcon, MatMenu, MatMenuItem, MatMenuTrigger, TranslateModule],
  selector: 'app-filter-card-add',
  styleUrl: './filter-card-add.component.scss',
  templateUrl: './filter-card-add.component.html',
})
export class FilterCardAddComponent<Name extends string> implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly pool = input.required<TFilterCardFieldConfig<Name>[]>();
  /** The manager's shared `oneOpenFormAtATime$` — see its doc comment. `null` is a safe no-op default
   * for any future consumer that doesn't wire it up. */
  public readonly oneOpenFormAtATime$ = input<Subject<symbol> | null>(null);
  public readonly add = output<{ name: Name; value: unknown }>();
  /**
   * The field currently being drafted, or `null` once picking ends for any reason — lets
   * `FilterCardComponent` skip rendering a *second*, real chip for the same field while this component
   * is already showing it (see `pendingChange`: picking a field activates it immediately).
   */
  public readonly draftingFieldChange = output<Name | null>();

  public readonly mode = signal<'button' | 'picking'>('button');
  public readonly pickedField = signal<TFilterCardFieldConfig<Name> | null>(null);

  private readonly fieldInput = viewChild(FilterCardFieldInputComponent);
  /** This component's own broadcast identity, for `oneOpenFormAtATime$` — stable for its lifetime. */
  private readonly identity = Symbol();

  public ngOnInit(): void {
    // Deferred to `ngOnInit` (rather than subscribing in the constructor) because `oneOpenFormAtATime$`
    // is an input — reading it before Angular has bound the real value would silently subscribe to the
    // default `null` instead.
    this.oneOpenFormAtATime$()
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((identity) => {
        // Own broadcast (`pickField` below) always arrives here too — `identity === this.identity`
        // then, so this correctly skips closing itself. `false`: an implicit close (another chip
        // opened, or Apply was clicked) keeps whatever was hidden-pushed so far — only the explicit ×
        // (`dismiss(true)`) discards it.
        if (identity !== this.identity && this.mode() === 'picking') {
          this.dismiss(false);
        }
      });
  }

  public onAddClick(): void {
    if (this.pool().length === 0) {
      this.snackbarService.open(this.translate.instant('system.message.allFiltersAdded'));
    }
  }

  /**
   * Broadcasting imperatively here — rather than from an `effect()` watching for `mode() === 'picking'`
   * — is deliberate, not a style choice: see the matching note on `FilterCardChipComponent.chipClick`.
   * An `effect()` only sees the value at flush time, and `oneOpenFormAtATime$.next(...)` is
   * synchronous and re-entrant — a subscriber can write signals of its own (e.g. a chip closing
   * itself) before this effect would ever get a chance to run, which in testing caused this
   * component's *own* already-queued effect to flush a second time and race ahead of — and
   * ultimately suppress — a chip's own broadcast for entering edit mode. Firing synchronously with
   * the write closes that race.
   */
  public pickField(field: TFilterCardFieldConfig<Name>): void {
    this.pickedField.set(field);
    this.mode.set('picking');
    this.oneOpenFormAtATime$()?.next(this.identity);
    this.draftingFieldChange.emit(field.name);

    if (field.type !== 'dropdown') {
      return;
    }

    // The input only exists after the change detection that follows this click.
    setTimeout(() => this.fieldInput()?.openDropdown());
  }

  /**
   * Every intermediate value change (e.g. one checkbox in a still-open multi-select) — mirrors
   * `commit` below but never collapses the picker, so the field activates and picks up its live value
   * (making the page-level Apply bar appear) while the user is still selecting. Digital-boards calls
   * this "hidden submission".
   */
  public onPendingChange(value: unknown): void {
    const field = this.pickedField();

    if (!field) {
      return;
    }

    this.add.emit({ name: field.name, value });
  }

  public commit(value: unknown): void {
    const field = this.pickedField();

    if (!field) {
      return;
    }

    this.add.emit({ name: field.name, value });
    this.dismiss();
  }

  /**
   * `discard`, true only for the explicit × button: since `onPendingChange` may already have
   * activated the field (it wasn't active before this draft session — `pool()` only offers inactive
   * fields), cancelling has to undo that by pushing an empty value through the same channel a chip's
   * own × uses (`FilterCardComponent.onChipRemove`) — safe to call unconditionally, even if nothing
   * was ever hidden-pushed (deactivating an already-inactive field is a no-op).
   */
  public dismiss(discard = false): void {
    const field = this.pickedField();

    this.mode.set('button');
    this.pickedField.set(null);
    this.draftingFieldChange.emit(null);

    if (discard && field) {
      this.add.emit({ name: field.name, value: null });
    }
  }
}
